import {
    Flex,
    Divider,
    HStack,
    Link,
    Stack,
    Box,
    IconButton,
    Text,
    DataList,
    DataItem,
} from '@cad-arc';
import { NearbyGeoCautionDto } from '@mark43/rms-api';
import { getNearbyCautionsGroupedByFullAddress } from './helpers';
import { CautionPill } from '../../../cautions/components/caution-pill';
import { useEsriMapView } from '../../hooks/use-esri-map-view';
import { isValidLatLng } from '../../../location/helpers';
import type { GeoPoint } from '../../../location/types';
import { CommonSize, getOneSizeSmaller } from '../../../common/helpers/utils';
import { getDataItemText } from '../../../common/helpers/component-utils';

const CautionDistanceBlock = ({
    isValidLatLng = true,
    distanceFeet,
    size,
}: {
    isValidLatLng?: boolean;
    distanceFeet: number;
    size?: CommonSize;
}) => {
    const oneSizeSmaller = getOneSizeSmaller(size);

    return (
        <Stack direction="row" alignItems="center">
            <Text color={isValidLatLng ? 'brand' : 'unset'} fontSize={size}>{`${Math.round(
                distanceFeet
            )}ft`}</Text>
            <IconButton
                disabled={!isValidLatLng}
                aria-label="Location"
                icon="Crosshairs"
                variant="ghost"
                size={oneSizeSmaller}
                data-testid="MAP_CROSSHAIRS_BUTTON"
            />
        </Stack>
    );
};

type NearbyCautionsSectionProps = {
    cautions?: NearbyGeoCautionDto[];
    setSelectedNearbyGeoCautionCoordinates: (selectedNearbyGeoCautionCoordinates: GeoPoint) => void;
    size?: CommonSize;
};

export function NearbyCautionsSection({
    cautions,
    setSelectedNearbyGeoCautionCoordinates,
    size,
}: NearbyCautionsSectionProps): React.ReactElement {
    const { goTo } = useEsriMapView();

    const cautionsGroupedByFullAddress = getNearbyCautionsGroupedByFullAddress(cautions || []);
    const isNearbyCautions = cautionsGroupedByFullAddress.length > 0;

    const handleLocationLinkOrBtnClick = (latitude: number, longitude: number) => {
        goTo({ latitude, longitude });
        setSelectedNearbyGeoCautionCoordinates({ latitude, longitude });
    };

    const smallerSize = getOneSizeSmaller(size);

    return (
        <>
            <Flex
                direction="column"
                padding="12px 18px"
                data-testid="MAP_LOCATION_PIN_NEARBY_CAUTIONS_SECTION"
            >
                <Box marginBottom={3}>
                    <Text fontSize={size} fontWeight="bold">
                        {`Nearby Cautions${cautions?.length ? ` (${cautions?.length})` : ''}`}
                    </Text>
                </Box>
                {isNearbyCautions ? (
                    <Flex direction="column">
                        {cautionsGroupedByFullAddress.map(
                            ({
                                cautions,
                                cautionFullAddress,
                                distanceFeet,
                                latitude,
                                longitude,
                            }) => {
                                return (
                                    <Box key={cautionFullAddress} marginBottom={3}>
                                        <DataList direction="column" variant="stacked">
                                            <HStack
                                                justifyContent="space-between"
                                                alignItems="flex-start"
                                                gap={4}
                                                marginBottom={1}
                                            >
                                                <DataItem
                                                    dataKey={
                                                        <Text fontSize={smallerSize}>Location</Text>
                                                    }
                                                    style={{ wordBreak: 'break-word' }}
                                                    data-testid="MAP_LOCATION_PIN_NEARBY_CAUTIONS_ADDRESS"
                                                >
                                                    {getDataItemText(size, cautionFullAddress)}
                                                </DataItem>

                                                <DataItem
                                                    dataKey={
                                                        <Text fontSize={smallerSize}>Distance</Text>
                                                    }
                                                    style={{
                                                        wordBreak: 'break-word',
                                                        minWidth: 'fit-content',
                                                    }}
                                                    data-testid="MAP_LOCATION_PIN_NEARBY_CAUTIONS_DISTANCE"
                                                >
                                                    {isValidLatLng(latitude, longitude) ? (
                                                        <Link
                                                            onClick={() =>
                                                                handleLocationLinkOrBtnClick(
                                                                    latitude!,
                                                                    longitude!
                                                                )
                                                            }
                                                        >
                                                            <CautionDistanceBlock
                                                                distanceFeet={distanceFeet}
                                                                size={size}
                                                            />
                                                        </Link>
                                                    ) : (
                                                        <CautionDistanceBlock
                                                            isValidLatLng={false}
                                                            distanceFeet={distanceFeet}
                                                            size={size}
                                                        />
                                                    )}
                                                </DataItem>
                                            </HStack>
                                        </DataList>
                                        {cautions.map((caution) => {
                                            return (
                                                <CautionPill
                                                    key={caution.id}
                                                    caution={caution}
                                                    size={size == 'lg' ? 'md' : 'sm'}
                                                />
                                            );
                                        })}
                                    </Box>
                                );
                            }
                        )}
                    </Flex>
                ) : (
                    <Text data-testid="MAP_LOCATION_NO_NEARBY_CAUTIONS_MESSAGE">
                        {cautions ? 'No Nearby Cautions Found' : 'Loading cautions...'}
                    </Text>
                )}
            </Flex>
            <Divider />
        </>
    );
}
